import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Label,
} from '@arcadiapower/design-system';
import { Eye, EyeOff } from 'lucide-react';
import { ty } from '@client/styles/typography';
import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AcquisitionTemplateOutput,
  AcquisitionTemplateOutputField,
  Provider,
  Organization,
  FormSchema,
  FormFields,
} from '@arc-connect/schema';
import { Trans, useTranslation } from 'react-i18next';
import { useController, Control, useForm, UseControllerProps } from 'react-hook-form';
import { parseErrorMessage } from '@client/config/errors';
import { ContentSectionFullHeight, Footer } from '@client/components/connect-container';
import { trackEvent } from '@client/utils/analytics';
import { ENABLE_GUEST_ACCESS_FEATURE } from '@client/config/constants';
import { Form } from '../form';
import { Disclaimer } from '../disclaimer';
import { OrganizationTerms } from '../organization-terms';
import { ForgotPasswordLink } from '../forgot-password-link';
import { Stack, Fields, NestedFields, CorrelationIdGroup } from './credentials-form.style';

const ReactHookFormInput = ({
  attribute,
  required,
  label,
  innerIndex,
  length,
  specialCase,
  attributeCount,
  control,
}: AcquisitionTemplateOutputField & {
  attributeCount: number;
  // We cannot type control because the labels rely on a dynamic number
  control: Control<any>;
  innerIndex?: number;
}) => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.credentialsForm' });
  const name = useMemo(() => {
    // the first value is just the label without the number
    const derivedCount = attributeCount === 1 ? '' : attributeCount;
    return `${attribute}${derivedCount}`;
  }, [attribute, attributeCount]);

  const isInnerField = innerIndex !== undefined;
  const appliedLabel = useMemo(() => {
    let appliedLabel = label || attribute;
    if (isInnerField) {
      appliedLabel = t('partialLabel', { label: appliedLabel, position: innerIndex + 1 });
    }
    return appliedLabel;
  }, [label, attribute, innerIndex, isInnerField, t]);

  const rules: UseControllerProps['rules'] = useMemo(() => {
    const result: UseControllerProps['rules'] = {};
    if (required) {
      result.required = t('validations.fieldRequiredErrorMessage', { fieldName: appliedLabel });
    }
    if (attribute === 'accountNumber') {
      result.deps = 'confirmAccountNumber';
    }
    if (attribute === 'confirmAccountNumber') {
      result.validate = (value, formValues) =>
        value === formValues.accountNumber || t('validations.fieldConfirmationFailsErrorMessage');
    }
    if (specialCase === 'conEdAccountNumber') {
      result.pattern = {
        value: /^\d{11}$/,
        message: t('validations.fieldConEdAccountNumberErrorMessage'),
      };
    }
    if (length) {
      const lengthValidation = {
        value: length,
        message: t('validations.fieldLengthErrorMessage', { fieldName: appliedLabel, length }),
      };
      result.minLength = lengthValidation;
      result.maxLength = lengthValidation;
    }

    return result;
  }, [required, length, appliedLabel, t, attribute, specialCase]);

  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  useEffect(() => {
    if (fieldState.isDirty) {
      trackEvent(`credentials-field-modified-${attribute}`, {
        label: appliedLabel,
        backendField: name,
      });
    }
  }, [fieldState.isDirty, attribute, appliedLabel, name]);

  const maxLength = specialCase === 'conEdAccountNumber' ? 11 : length;

  if (attribute === 'password') {
    return (
      <Field>
        {!isInnerField && <FieldLabel htmlFor={field.name}>{appliedLabel}</FieldLabel>}
        <InputGroup>
          <InputGroupInput
            id={field.name}
            aria-label={isInnerField ? appliedLabel : undefined}
            type={isPasswordVisible ? 'text' : 'password'}
            maxLength={maxLength}
            name={field.name} // send down the input name
            onChange={e => field.onChange(e.target.value)} // send value to hook form
            onBlur={field.onBlur} // notify when input is touched/blur
            value={field.value ?? ''} // input value
          />
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              type="button"
              size="icon-xs"
              className="!text-foreground-primary"
              onClick={() => setIsPasswordVisible(visible => !visible)}
              aria-label={isPasswordVisible ? t('inputs.hidePassword') : t('inputs.showPassword')}
            >
              {isPasswordVisible ? <EyeOff /> : <Eye />}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
        {fieldState.error?.message ? <FieldError>{fieldState.error.message}</FieldError> : null}
      </Field>
    );
  }
  const errorMessage = fieldState.error?.message;
  return (
    <Field data-invalid={errorMessage ? true : undefined}>
      {!isInnerField && <FieldLabel htmlFor={field.name}>{appliedLabel}</FieldLabel>}
      <Input
        id={field.name}
        aria-label={isInnerField ? appliedLabel : undefined}
        aria-invalid={errorMessage ? true : undefined}
        maxLength={maxLength}
        type="text"
        name={field.name}
        onChange={event => field.onChange(event.target.value)}
        onBlur={field.onBlur}
        value={field.value ?? ''}
      />
      {errorMessage ? <FieldError>{errorMessage}</FieldError> : null}
    </Field>
  );
};

const GuestAccessLearnMoreLink = ({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}): JSX.Element => (
  <Button variant="link" asChild className="h-auto rounded-sm p-0">
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  </Button>
);

export type Props = {
  acquisitionTemplate: AcquisitionTemplateOutput;
  onSubmit: (templateFields: FormFields) => void;
  pending: boolean;
  templateFields?: FormFields;
  error?: unknown;
  showCorrelationIdField?: boolean;
  organization: Organization;
  provider: Provider;
};

export const CredentialsForm = ({
  acquisitionTemplate,
  templateFields,
  onSubmit,
  pending,
  error,
  showCorrelationIdField,
  organization,
  provider,
}: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.credentialsForm' });
  const { t: guestAccessCopy } = useTranslation('connect', { keyPrefix: 'components.guestAccess' });
  const guestAccessLearnMoreLink = 'https://docs.arcadia.com/docs/integrate-connect';

  const { handleSubmit, control } = useForm<FormFields>({
    defaultValues: templateFields,
  });

  const handleFormSubmit = useCallback(
    (fieldValues: unknown) => {
      // We are adding fields at runtime, so we will parse the schema
      // to get rid of the ones we don't need (e.g. account number confirmation)
      const formFieldValues = FormSchema.parse(fieldValues);
      onSubmit(formFieldValues);
    },
    [onSubmit]
  );

  const fieldPositions = {
    password: 0,
    username: 0,
    accountNumber: 0,
    confirmAccountNumber: 0,
    correlationId: 0,
  };

  const correlationIdLink = 'https://docs.arcadia.com/docs/correlationids';
  const description = provider.isThirdPartyPortalSupported
    ? t('descriptionThirdPartyPortal')
    : t('description', { providerName: provider.name });
  const shouldShowGuestAccessCopy = !!(
    ENABLE_GUEST_ACCESS_FEATURE &&
    organization.isAutomatedGuestAccessConfigurationEnabled &&
    provider.multiFactorAuthentication.isGuestAccessSupported
  );

  const buttonText =
    templateFields && provider.isRealTimeCredentialValidationSupported
      ? t('inputs.resubmitButton')
      : t('inputs.submitButton');

  return (
    <>
      <ContentSectionFullHeight>
        <Stack>
          <h3 className={ty('heading900')} data-testid="providerName">
            {provider.name}
          </h3>
          <p className={ty('default')}>{description}</p>
          <Form onSubmit={handleSubmit(handleFormSubmit)} id="credentialsForm">
            <Fields>
              {acquisitionTemplate.map((input, outerIndex) => {
                if (Array.isArray(input)) {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={outerIndex}>
                      <Label>{input[0].label}</Label>
                      <NestedFields>
                        {input.map((nestedInput, innerIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Fragment key={innerIndex}>
                            <ReactHookFormInput
                              {...nestedInput}
                              attributeCount={++fieldPositions[nestedInput.attribute]}
                              control={control}
                              innerIndex={innerIndex}
                            />
                            {/* Add a dash if this is not the last input */}
                            {innerIndex + 1 !== input.length && (
                              <span className={ty('heading500')}>-</span>
                            )}
                          </Fragment>
                        ))}
                      </NestedFields>
                    </Fragment>
                  );
                }
                return (
                  <ReactHookFormInput
                    {...input}
                    attributeCount={++fieldPositions[input.attribute]}
                    control={control}
                    // eslint-disable-next-line react/no-array-index-key
                    key={outerIndex}
                  />
                );
              })}
              {showCorrelationIdField ? (
                <CorrelationIdGroup>
                  <ReactHookFormInput
                    required={false}
                    label={t('inputs.correlationId')}
                    attribute="correlationId"
                    attributeCount={++fieldPositions.correlationId}
                    control={control}
                  />
                  <Button
                    variant="link"
                    size="mini"
                    asChild
                    className="h-auto self-start rounded-sm p-0"
                  >
                    <a href={correlationIdLink} target="_blank" rel="noreferrer">
                      {t('correlationIdLink')}
                    </a>
                  </Button>
                </CorrelationIdGroup>
              ) : null}
              {!!error && <FieldError>{parseErrorMessage(error)}</FieldError>}
            </Fields>
          </Form>
          <ForgotPasswordLink provider={provider} />
          {shouldShowGuestAccessCopy ? (
            <p className={ty('paragraph400')}>
              <Trans
                t={guestAccessCopy}
                i18nKey="description"
                components={[
                  <span key="0" style={{ fontWeight: 'bolder' }} />,
                  <GuestAccessLearnMoreLink key="1" href={guestAccessLearnMoreLink} />,
                ]}
              />
            </p>
          ) : null}
          <Disclaimer />
        </Stack>
      </ContentSectionFullHeight>
      <Footer>
        <OrganizationTerms organization={organization} />
        <Button
          type="submit"
          loading={pending}
          className="w-full"
          form="credentialsForm"
          variant="inverse"
        >
          {buttonText}
        </Button>
      </Footer>
    </>
  );
};
